// Staff photos and rider plates on the admin side — 0279.
//
// PHOTOS: the bucket is private (0150); a photo is shown only through a
// short-lived signed URL, minted here in ONE batch for the whole list. Storage
// RLS decides what may be signed: an admin any, a Hub Manager only their own
// hub's staff (0279). A photo the caller may not see comes back as an error
// for that path and simply falls through to initials — never a broken image.
//
// PLATES: `staff_vehicles` (0150) — read under its RLS (a Hub Manager sees their
// hub's riders), written only through `set_rider_vehicle` (0279), which owns the
// "a rider, in YOUR hub" rule and the audit.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { displayName } from "@bednbody/logic";
import { supabase } from "@/lib/supabase";

const BUCKET = "staff-photos";
/** Ten minutes, refreshed at five — the partner app's posture (staffPhoto.ts). */
const SIGNED_URL_TTL_SECONDS = 600;
const VEHICLES_KEY = ["staff-vehicles"] as const;

/** Signed photo URLs for these staff, keyed by staff id. Absent = no photo to show. */
export function useStaffPhotoUrls(staffIds: readonly string[]) {
  const ids = [...new Set(staffIds)].sort();
  return useQuery({
    queryKey: ["staff-photo-urls", ids],
    enabled: ids.length > 0,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data: rows, error } = await supabase
        .from("staff")
        .select("id, photo_path")
        .in("id", ids)
        .not("photo_path", "is", null);
      if (error) throw error;
      const withPhoto = (rows ?? []).filter((row): row is { id: string; photo_path: string } =>
        Boolean(row.photo_path),
      );
      if (withPhoto.length === 0) return new Map();

      const { data: signed, error: signError } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(
          withPhoto.map((row) => row.photo_path),
          SIGNED_URL_TTL_SECONDS,
        );
      // A failed batch is not an outage: everyone shows initials instead.
      if (signError || !signed) return new Map();
      const urlByPath = new Map(
        signed.flatMap((entry) =>
          entry.signedUrl && entry.path ? [[entry.path, entry.signedUrl] as const] : [],
        ),
      );
      return new Map(
        withPhoto.flatMap((row) => {
          const url = urlByPath.get(row.photo_path);
          return url ? [[row.id, url] as const] : [];
        }),
      );
    },
    staleTime: (SIGNED_URL_TTL_SECONDS / 2) * 1000,
    refetchInterval: (SIGNED_URL_TTL_SECONDS / 2) * 1000,
    retry: false,
  });
}

/**
 * Up to four therapists/riders who have a photo — for the live map simulator,
 * which shows them as extra pins so faces can be reviewed. RLS scopes it (a Hub
 * Manager: their hub). Read only while the simulator is on.
 */
export function useStaffWithPhotos(enabled: boolean) {
  return useQuery({
    queryKey: ["staff-with-photos"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, role")
        .not("photo_path", "is", null)
        .eq("active", true)
        .in("role", ["therapist", "rider"])
        .order("full_name")
        .limit(4);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        name: displayName(row.full_name) ?? row.full_name,
        role: row.role as "therapist" | "rider",
      }));
    },
    staleTime: 60_000,
  });
}

export interface RiderVehicle {
  plate: string;
  vehicleType: string;
}

/** Every rider's current plate the caller may see, keyed by staff id. */
export function useRiderVehicles() {
  return useQuery({
    queryKey: VEHICLES_KEY,
    queryFn: async (): Promise<Map<string, RiderVehicle>> => {
      const { data, error } = await supabase
        .from("staff_vehicles")
        .select("staff_id, plate, vehicle_type");
      if (error) throw error;
      return new Map(
        (data ?? []).map((row) => [
          row.staff_id,
          { plate: row.plate, vehicleType: row.vehicle_type },
        ]),
      );
    },
    staleTime: 60_000,
  });
}

/** Set a rider's plate — their Hub Manager or an admin (0279). */
export function useSetRiderVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { riderId: string; plate: string; vehicleType: string }) => {
      const { error } = await supabase.rpc("set_rider_vehicle", {
        p_rider_id: input.riderId,
        p_plate: input.plate,
        p_vehicle_type: input.vehicleType,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: VEHICLES_KEY }),
  });
}
